import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, ListChecks, ArrowUpRight } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { KpiCard, formatInt } from "@/components/reports/KpiCard";

const WO_STATUS_LABELS = {
  pending: "Pendientes",
  in_progress: "En progreso",
  paused: "En pausa",
  completed: "Completadas",
  cancelled: "Canceladas",
};

const SO_STATUS_LABELS = {
  recibido: "Recibidos",
  en_diagnostico: "En diagnóstico",
  pendiente_autorizacion: "Pend. autorización",
  autorizado: "Autorizados",
  en_proceso: "En proceso",
  pausa: "En pausa",
  listo_para_entrega: "Listo entrega",
  entregado: "Entregados",
};

export function TechnicianDashboard() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/reports/technician-summary");
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const myBy = data?.my_work_orders_by_status || {};
  const recent = data?.recent_work_orders || [];
  const opsCounts = data?.orders_by_status || {};
  const logs = data?.recent_status_changes || [];

  return (
    <div className="space-y-6" data-testid="page-dashboard" data-variant="technician">
      <div data-testid="page-technician-dashboard" className="hidden" aria-hidden />
      <PageHeader
        title={`Hola, ${session?.user?.full_name?.split(" ")[0] || "técnico"}`}
        description="Tu panel operativo — órdenes asignadas y estatus del taller."
        actions={
          <Badge variant="outline" className="border-tenant text-tenant">
            {session?.user?.role_name || "técnico"}
          </Badge>
        }
      />

      {/* My WOs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tech-kpi-grid">
        <KpiCard
          label="Mis órdenes"
          value={formatInt(data?.my_work_orders_count)}
          icon={ClipboardList}
          testId="tech-kpi-total"
          loading={loading}
        />
        {Object.entries(WO_STATUS_LABELS).map(([k, label]) => (
          <KpiCard
            key={k}
            label={label}
            value={formatInt(myBy[k])}
            icon={ListChecks}
            testId={`tech-kpi-${k}`}
            loading={loading}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent WOs */}
        <Card className="lg:col-span-2 border-border" data-testid="card-my-wos">
          <CardHeader className="border-b border-border flex flex-row items-start justify-between">
            <div>
              <CardTitle className="tracking-tight">Mis órdenes de trabajo</CardTitle>
              <CardDescription>Últimas 10 asignadas a ti.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : recent.length ? (
              <ul className="divide-y divide-border">
                {recent.map((w) => (
                  <li
                    key={w.id}
                    className="p-4 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/ordenes-de-servicio/${w.service_order_id}`)}
                    data-testid={`tech-wo-${w.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{w.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {w.folio || "Folio ?"} · {SO_STATUS_LABELS[w.so_status] || w.so_status}
                      </div>
                    </div>
                    <Badge variant="secondary" className="capitalize">
                      {WO_STATUS_LABELS[w.work_status] || w.work_status}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-6 text-sm text-muted-foreground">
                No tienes órdenes de trabajo asignadas.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ops snapshot */}
        <Card className="border-border" data-testid="card-tech-ops">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Estatus del taller</CardTitle>
            <CardDescription>Foto operativa de tu sucursal.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {Object.entries(SO_STATUS_LABELS).map(([k, label]) => (
                <li key={k} className="p-3 flex items-center justify-between" data-testid={`tech-status-${k}`}>
                  <span className="text-sm text-slate-700">{label}</span>
                  <span className="text-sm font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {opsCounts[k] || 0}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Recent activity */}
      <Card className="border-border" data-testid="card-tech-logs">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight">Cambios de estatus recientes</CardTitle>
          <CardDescription>Últimos movimientos operativos.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : logs.length ? (
            <ul className="divide-y divide-border">
              {logs.map((l, idx) => (
                <li
                  key={idx}
                  className="p-3 flex items-center gap-3 text-sm hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/ordenes-de-servicio/${l.service_order_id}`)}
                  data-testid={`tech-log-${idx}`}
                >
                  <ArrowUpRight className="h-4 w-4 text-slate-400" />
                  <div className="flex-1">
                    <div className="font-medium">{l.folio || l.service_order_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {(SO_STATUS_LABELS[l.previous_status] || l.previous_status || "—")} →{" "}
                      {SO_STATUS_LABELS[l.new_status] || l.new_status}
                      {l.changed_by_name ? ` · por ${l.changed_by_name}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Sin cambios recientes.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
